import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, Users, FileCheck, Calendar, Euro, Award, Clock, MapPin, Download, Loader2, BellRing, Timer, AlertTriangle, Zap, Headphones, Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed } from "lucide-react";
import api, { STATUS_LABELS, getUser } from "@/lib/api";
import { toast } from "sonner";
import QuickEstimateSimulator from "@/components/QuickEstimateSimulator";
import DepositSummaryPanel from "@/components/DepositSummaryPanel";
import PerformanceCompactWidget from "@/components/PerformanceCompactWidget";
import AlertsWidget from "@/components/AlertsWidget";
import ConsultorRTsCard from "@/components/ConsultorRTsCard";
import TeamVolumeWidget from "@/components/TeamVolumeWidget";

const PERIODS = [
  { k: "today", t: "Hoje" },
  { k: "week", t: "Semana" },
  { k: "month", t: "Mês corrente" },
  { k: "all", t: "Total" },
];

const REFRESH_MS = 60000;

// Cycle through pastel palette. `tone` overrides if provided.
const KPI_TONES = ["shift-kpi-mint", "shift-kpi-peach", "shift-kpi-lilac", "shift-kpi-blue"];

const Stat = ({ label, value, icon: Icon, hint, testid, tone, index = 0 }) => {
  const toneClass = tone || KPI_TONES[index % KPI_TONES.length];
  return (
    <div data-testid={testid} className={`shift-kpi-tile ${toneClass}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="kpi-label">{label}</div>
        <Icon className="w-4 h-4 opacity-60" strokeWidth={1.6} />
      </div>
      <div className="kpi-value">{value}</div>
      {hint && <div className="text-xs opacity-70 mt-1.5">{hint}</div>}
    </div>
  );
};

const SLA_STYLES = {
  ok:      { badge: "bg-[hsl(var(--success-bg))] text-[hsl(var(--success))] border-[hsl(var(--success))]/25", label: "No prazo" },
  warn:    { badge: "bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning))] border-[hsl(var(--warning))]/25", label: "Atenção" },
  urgent:  { badge: "bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning))] border-[hsl(var(--warning))]/25", label: "Urgente" },
  overdue: { badge: "bg-[hsl(var(--danger-bg))] text-[hsl(var(--danger))] border-[hsl(var(--danger))]/25", label: "SLA excedido" },
};

const SLA_TRACK_COLOR = {
  ok: "bg-emerald-500",
  warn: "bg-amber-500",
  urgent: "bg-orange-500",
  overdue: "bg-red-500",
};

function SlaQueue({ items }) {
  if (!items) return null;
  const overdue = items.filter(i => i.sla_level === "overdue").length;
  const urgent = items.filter(i => i.sla_level === "urgent").length;

  return (
    <div className="bg-card border border-border rounded-2xl p-6 lg:p-8 mb-8" data-testid="sla-queue">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center">
            <Timer className="w-4 h-4 text-foreground" />
          </div>
          <div>
            <h3 className="font-ui text-lg text-foreground">Fila de resposta · SLA 15 minutos</h3>
            <div className="text-xs text-muted-foreground">Leads a aguardar primeiro contacto pelo atendimento</div>
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs">
          {overdue > 0 && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[hsl(var(--danger))]/30 bg-[hsl(var(--danger-bg))] text-[hsl(var(--danger))] font-semibold" data-testid="sla-overdue-count">
              <AlertTriangle className="w-3.5 h-3.5" /> {overdue} excedido{overdue > 1 ? "s" : ""}
            </span>
          )}
          {urgent > 0 && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#FDBA74] bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning))] font-semibold">
              <BellRing className="w-3.5 h-3.5" /> {urgent} urgente{urgent > 1 ? "s" : ""}
            </span>
          )}
        </div>
      </div>

      {items.length === 0 && (
        <div className="text-sm text-muted-foreground py-6 text-center" data-testid="sla-empty">
          Sem leads pendentes. Excelente ritmo de resposta.
        </div>
      )}

      {items.length > 0 && (
        <div className="space-y-2">
          {items.slice(0, 8).map(l => {
            const s = SLA_STYLES[l.sla_level] || SLA_STYLES.ok;
            const track = SLA_TRACK_COLOR[l.sla_level] || SLA_TRACK_COLOR.ok;
            const pct = Math.min(100, Math.round((l.elapsed_minutes / 15) * 100));
            const c = l.qualification?.contact || {};
            const loc = l.qualification?.location || {};
            return (
              <Link key={l.id} to={`/leads/${l.id}`} data-testid={`sla-row-${l.id}`}
                className="flex items-center gap-4 px-4 py-3 rounded-xl border border-border hover:border-border hover:bg-muted/40 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground truncate">{c.name || "Contacto sem nome"}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {c.phone || "—"}{loc.municipality ? ` · ${loc.municipality}` : ""}
                  </div>
                </div>
                <div className="hidden sm:block w-40">
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                    <div className={`h-full ${track} transition-all`} style={{ width: `${pct}%` }} />
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mt-1 tabular-nums">
                    {l.elapsed_minutes} / 15 min
                  </div>
                </div>
                <span className={`inline-flex text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-full border ${s.badge}`}>
                  {s.label}
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PbxScoreCard({ pbx }) {
  if (!pbx) return null;
  const today = pbx.today || {};
  const yesterday = pbx.yesterday || {};
  const trend = pbx.trend || {};
  // Hide widget entirely when PBX has no calls today or yesterday — the
  // integration is likely not configured yet in this tenant.
  if ((today.total || 0) === 0 && (yesterday.total || 0) === 0) return null;

  const rate = today.answer_rate_pct;
  const rateColor = rate == null
    ? "text-muted-foreground"
    : rate >= 80 ? "text-[#059669]"
    : rate >= 50 ? "text-[hsl(var(--warning))]"
    : "text-[hsl(var(--danger))]";
  const rateBarColor = rate == null
    ? "bg-muted"
    : rate >= 80 ? "bg-[#059669]"
    : rate >= 50 ? "bg-[#B45309]"
    : "bg-[#B91C1C]";
  const deltaPct = trend.total_delta_pct;
  const deltaSign = (trend.total_delta || 0) > 0 ? "+" : "";
  const talkMinutes = Math.round((today.talk_seconds || 0) / 60);

  return (
    <div className="bg-card border border-border rounded-2xl p-6 lg:p-8 mb-8" data-testid="pbx-score-card">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center">
            <Phone className="w-4 h-4 text-foreground" />
          </div>
          <div>
            <h3 className="font-ui text-lg text-foreground">Score PBX · Chamadas hoje</h3>
            <div className="text-xs text-muted-foreground">Central telefónica Yeastar Linkus — actualiza a cada 60s</div>
          </div>
        </div>
        <Link
          to="/settings/linkus"
          data-testid="link-linkus-settings"
          className="text-xs text-foreground hover:underline font-semibold">
          Configuração PBX →
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-5">
        <div className="bg-muted/40 border border-border rounded-xl p-4" data-testid="pbx-total-today">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1">Total chamadas</div>
          <div className="font-ui text-3xl tracking-tight text-foreground tabular-nums">{today.total || 0}</div>
          <div className="text-xs text-muted-foreground mt-1">
            {deltaPct == null
              ? <span className="text-muted-foreground">vs. ontem: —</span>
              : (
                <span className={`${(trend.total_delta || 0) >= 0 ? "text-[#059669]" : "text-[hsl(var(--danger))]"} font-semibold`}>
                  {deltaSign}{trend.total_delta} ({deltaSign}{deltaPct}%)
                </span>
              )}
          </div>
        </div>

        <div className="bg-muted/40 border border-border rounded-xl p-4" data-testid="pbx-answer-rate">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1">Taxa de atendimento</div>
          <div className={`font-ui text-3xl tracking-tight tabular-nums ${rateColor}`}>
            {rate == null ? "—" : `${rate}%`}
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
            <div className={`h-full ${rateBarColor} transition-all`} style={{ width: `${rate == null ? 0 : Math.min(rate, 100)}%` }} />
          </div>
          <div className="text-xs text-muted-foreground mt-1 tabular-nums">
            {today.answered || 0} atendidas / {today.total || 0}
          </div>
        </div>

        <div className="bg-muted/40 border border-border rounded-xl p-4" data-testid="pbx-outbound">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1">
            <PhoneOutgoing className="w-3 h-3" /> Saída
          </div>
          <div className="font-ui text-3xl tracking-tight text-foreground tabular-nums">{today.outbound || 0}</div>
          <div className="text-xs text-muted-foreground mt-1">Chamadas efectuadas</div>
        </div>

        <div className="bg-muted/40 border border-border rounded-xl p-4" data-testid="pbx-inbound">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1">
            <PhoneIncoming className="w-3 h-3" /> Entrada
          </div>
          <div className="font-ui text-3xl tracking-tight text-foreground tabular-nums">{today.inbound || 0}</div>
          <div className="text-xs text-muted-foreground mt-1">Chamadas recebidas</div>
        </div>

        <div className="bg-muted/40 border border-border rounded-xl p-4" data-testid="pbx-missed">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1">
            <PhoneMissed className="w-3 h-3" /> Não atendidas
          </div>
          <div className={`font-ui text-3xl tracking-tight tabular-nums ${(today.missed || 0) > 0 ? "text-[hsl(var(--danger))]" : "text-foreground"}`}>{today.missed || 0}</div>
          <div className="text-xs text-muted-foreground mt-1 tabular-nums">
            {talkMinutes > 0 ? `${talkMinutes}m em conversa` : "—"}
          </div>
        </div>
      </div>
    </div>
  );
}

function PbxConsultantRanking({ ranking, period, onPeriodChange }) {
  if (!ranking) return null;
  const items = ranking.items || [];
  const unassigned = ranking.unassigned || null;
  const activeItems = items.filter((i) => i.total > 0);
  // Se ninguém tem chamadas no período (mesmo com utilizadores configurados)
  // e não há unassigned → esconde o card por completo.
  if (activeItems.length === 0 && (!unassigned || (unassigned.total || 0) === 0)) {
    return null;
  }
  const maxTotal = Math.max(1, ...items.map((i) => i.total || 0));
  const periods = [
    { key: "today", label: "Hoje" },
    { key: "week", label: "Semana" },
    { key: "month", label: "Mês" },
  ];

  return (
    <div className="bg-card border border-border rounded-2xl p-6 lg:p-8 mb-8" data-testid="pbx-ranking-card">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center">
            <Headphones className="w-4 h-4 text-foreground" />
          </div>
          <div>
            <h3 className="font-ui text-lg text-foreground">Ranking PBX · Chamadas por Consultor</h3>
            <div className="text-xs text-muted-foreground">Atribuído pela extensão Linkus de cada utilizador</div>
          </div>
        </div>
        <div className="inline-flex rounded-lg bg-muted p-1" data-testid="pbx-ranking-period-switch">
          {periods.map((p) => (
            <button
              key={p.key}
              onClick={() => onPeriodChange(p.key)}
              className={`px-3 py-1 text-xs font-semibold rounded-md transition ${period === p.key ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              data-testid={`pbx-ranking-period-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {activeItems.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6 text-center" data-testid="pbx-ranking-empty">
          Sem chamadas atribuídas neste período.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="pbx-ranking-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold border-b border-border/60">
                <th className="py-2 pr-3 w-8">#</th>
                <th className="py-2 pr-3">Consultor</th>
                <th className="py-2 pr-3 text-center">Ext.</th>
                <th className="py-2 pr-3 text-right">Total</th>
                <th className="py-2 pr-3 text-right">Atendidas</th>
                <th className="py-2 pr-3 text-right">Perdidas</th>
                <th className="py-2 pr-3 text-right">Taxa</th>
                <th className="py-2 pr-3 text-right">Conversa</th>
                <th className="py-2 pl-3 w-40"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => {
                const rate = it.answer_rate_pct;
                const rateColor = rate == null
                  ? "text-muted-foreground"
                  : rate >= 80 ? "text-[#059669]"
                  : rate >= 50 ? "text-[hsl(var(--warning))]"
                  : "text-[hsl(var(--danger))]";
                const barPct = it.total > 0 ? (it.total / maxTotal) * 100 : 0;
                const isZero = it.total === 0;
                return (
                  <tr
                    key={it.user_id}
                    className={`border-b border-border/40 ${isZero ? "opacity-60" : ""}`}
                    data-testid={`pbx-ranking-row-${it.extension}`}
                  >
                    <td className="py-2.5 pr-3 tabular-nums text-muted-foreground font-semibold">{idx + 1}</td>
                    <td className="py-2.5 pr-3">
                      <div className="font-medium text-foreground" data-testid="pbx-ranking-name">{it.name}</div>
                      <div className="text-[11px] text-muted-foreground">{it.role || "—"}</div>
                    </td>
                    <td className="py-2.5 pr-3 text-center tabular-nums font-mono text-xs text-muted-foreground">{it.extension}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums font-ui text-lg font-semibold" data-testid="pbx-ranking-total">{it.total}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums text-foreground">{it.answered}</td>
                    <td className={`py-2.5 pr-3 text-right tabular-nums ${it.missed > 0 ? "text-[hsl(var(--danger))]" : "text-muted-foreground"}`}>{it.missed}</td>
                    <td className={`py-2.5 pr-3 text-right tabular-nums font-semibold ${rateColor}`}>{rate == null ? "—" : `${rate}%`}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums text-muted-foreground">
                      {it.talk_minutes > 0 ? `${it.talk_minutes}m` : "—"}
                    </td>
                    <td className="py-2.5 pl-3">
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                        <div className="h-full bg-foreground transition-all" style={{ width: `${barPct}%` }} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {unassigned && unassigned.total > 0 && (
        <div className="mt-5 pt-4 border-t border-border/60" data-testid="pbx-ranking-unassigned">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-xs text-muted-foreground font-semibold">
                <span className="text-[hsl(var(--warning))]">⚠</span> Chamadas sem consultor associado
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                Extensões {unassigned.extensions.join(", ")} — configure em Utilizadores → Extensão Linkus
              </div>
            </div>
            <div className="text-sm text-foreground font-semibold tabular-nums">
              {unassigned.total} chamada{unassigned.total === 1 ? "" : "s"}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const [k, setK] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("month");
  const [downloading, setDownloading] = useState(false);
  const [slaQueue, setSlaQueue] = useState([]);
  const [slaFirst, setSlaFirst] = useState(null);
  const [pbx, setPbx] = useState(null);
  const [pbxRanking, setPbxRanking] = useState(null);
  const [pbxRankingPeriod, setPbxRankingPeriod] = useState("today");
  const [lastRefresh, setLastRefresh] = useState(null);

  const seenLeadIdsRef = useRef(new Set());
  const initializedRef = useRef(false);
  const sinceRef = useRef(new Date().toISOString());
  const overdueNotifiedRef = useRef(new Set());

  // Initial load & period switch (with spinner)
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/dashboard/kpis?period=${period}`)
      .then((r) => { if (!cancelled) setK(r.data); })
      .finally(() => { if (!cancelled) { setLoading(false); setLastRefresh(new Date()); } });
    // PBX metrics (best-effort; card só renderiza se houver dados PBX activos)
    api.get(`/linkus/dashboard-metrics`).then((r) => {
      if (!cancelled) setPbx(r.data);
    }).catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [period]);

  // Ranking PBX — fetch quando muda o período seleccionado
  useEffect(() => {
    let cancelled = false;
    api.get(`/linkus/consultant-ranking?period=${pbxRankingPeriod}`).then((r) => {
      if (!cancelled) setPbxRanking(r.data);
    }).catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [pbxRankingPeriod]);

  // Teams / Outlook out-of-band changes — fire ONE toast per unseen change
  // the first time the gestor lands on the dashboard, then mark them as
  // seen so we don't spam the toast area on every mount.
  // (The full history is always visible in the LeadTimeline block.)
  useEffect(() => {
    let cancelled = false;
    api.get("/me/teams-notifications")
      .then(async (r) => {
        if (cancelled) return;
        const items = r.data?.items || [];
        if (!items.length) return;
        items.forEach((it) => {
          const name = it.client_name || "Lead";
          const kind = it.kind;
          if (kind === "rescheduled") {
            toast.info(`Reunião reagendada · ${name}`, {
              description: `Antes: ${it.previous_scheduled_at || "—"} → Agora: ${it.new_scheduled_at || "—"}`,
              action: { label: "Abrir", onClick: () => { window.location.href = `/leads/${it.lead_id}`; } },
              duration: 8000,
            });
          } else if (kind === "cancelled_by_client") {
            toast.error(`Cliente recusou a reunião · ${name}`, {
              description: `Estava agendada para ${it.previous_scheduled_at || "—"}. Lead marcado como RT Cancelada.`,
              action: { label: "Abrir", onClick: () => { window.location.href = `/leads/${it.lead_id}`; } },
              duration: 10000,
            });
          } else {
            const desc = kind === "cancelled_deleted"
              ? "Evento eliminado do calendário do gestor."
              : "Reunião cancelada no Outlook.";
            toast.warning(`Reunião cancelada · ${name}`, {
              description: `${desc} Lead marcado como RT Cancelada.`,
              action: { label: "Abrir", onClick: () => { window.location.href = `/leads/${it.lead_id}`; } },
              duration: 10000,
            });
          }
        });
        // Mark them as seen so we don't re-fire next mount.
        const ids = items.map(i => i.id).filter(Boolean);
        if (ids.length) {
          try { await api.post("/me/teams-notifications/mark-seen", { ids }); }
          catch { /* silent */ }
        }
      })
      .catch(() => { /* silent — endpoint is best-effort */ });
    return () => { cancelled = true; };
  }, []);

  // Silent auto-refresh every 60s (KPIs + SLA queue + new leads detection)
  useEffect(() => {
    let cancelled = false;

    const tick = async () => {
      try {
        const [kpisRes, slaRes, sinceRes, slaFirstRes, pbxRes] = await Promise.all([
          api.get(`/dashboard/kpis?period=${period}`),
          api.get(`/leads/sla-queue`),
          api.get(`/leads/since?ts=${encodeURIComponent(sinceRef.current)}`),
          api.get(`/reports/sla-first-response?period=${period === 'all' ? 'month' : period}`),
          api.get(`/linkus/dashboard-metrics`).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;

        setK(kpisRes.data);
        setSlaQueue(slaRes.data.queue || []);
        setSlaFirst(slaFirstRes.data);
        setPbx(pbxRes.data);
        setLastRefresh(new Date());

        // Toast for genuinely new leads (skip the very first tick after mount)
        const incoming = sinceRes.data.leads || [];
        if (initializedRef.current) {
          incoming.forEach((l) => {
            if (!seenLeadIdsRef.current.has(l.id)) {
              const name = l.qualification?.contact?.name || "Nova lead";
              const municipality = l.qualification?.location?.municipality || "";
              toast.success(`Nova lead: ${name}`, {
                description: municipality ? `A aguardar atendimento · ${municipality}` : "A aguardar atendimento",
                action: { label: "Abrir", onClick: () => { window.location.href = `/leads/${l.id}`; } },
              });
            }
          });
        }
        incoming.forEach((l) => seenLeadIdsRef.current.add(l.id));
        if (sinceRes.data.server_now) sinceRef.current = sinceRes.data.server_now;

        // Toast for leads that just crossed the SLA overdue threshold
        (slaRes.data.queue || []).forEach((l) => {
          if (l.sla_level === "overdue" && !overdueNotifiedRef.current.has(l.id)) {
            overdueNotifiedRef.current.add(l.id);
            if (initializedRef.current) {
              const name = l.qualification?.contact?.name || "Lead";
              toast.error(`SLA excedido: ${name}`, {
                description: `${l.elapsed_minutes} min sem primeiro contacto`,
                action: { label: "Abrir", onClick: () => { window.location.href = `/leads/${l.id}`; } },
              });
            }
          }
        });

        initializedRef.current = true;
      } catch (e) {
        // silent
      }
    };

    tick(); // fire immediately on mount / period change so SLA & seed load fast
    const id = setInterval(tick, REFRESH_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [period]);

  const downloadReport = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/reports/weekly/pdf?period=${period}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Caring-Relatorio-${period}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Relatório descarregado");
    } catch(e){ toast.error("Erro ao gerar relatório"); } finally { setDownloading(false); }
  };

  if (loading || !k) return <div className="p-10 text-muted-foreground">A carregar...</div>;

  const currentLabel = PERIODS.find(p=>p.k===period)?.t;
  const refreshLabel = lastRefresh ? lastRefresh.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' }) : "—";

  return (
    <div className="p-8 lg:p-12 max-w-[1400px] mx-auto" data-testid="dashboard-root">
      <div className="mb-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2">Visão geral · {currentLabel}</div>
          <h1 className="font-display text-4xl lg:text-5xl tracking-tight text-foreground">Pipeline de clientes</h1>
        </div>
        <div className="flex items-center gap-2">
          <span className="hidden md:inline-flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid="last-refresh">
            <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse" />
            Atualizado às {refreshLabel}
          </span>
          <button data-testid="download-report" onClick={downloadReport} disabled={downloading}
            className="inline-flex items-center gap-2 bg-card hover:bg-muted border border-border text-muted-foreground rounded-md px-3 py-1.5 text-xs">
            {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Relatório PDF
          </button>
          <Link data-testid="dashboard-lead-script" to="/leads/script" className="hidden sm:inline-flex items-center gap-2 bg-card hover:bg-muted border border-border text-foreground rounded-md px-3 py-1.5 text-xs font-semibold">
            <Headphones className="w-3.5 h-3.5" /> Guião de Atendimento
          </Link>
          <Link data-testid="dashboard-new-lead" to="/leads/new" className="hidden sm:inline-flex bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-md px-4 py-2 text-sm transition-all font-heading tracking-tight">
            Nova qualificação
          </Link>
        </div>
      </div>

      <div className="flex items-center gap-1 mb-8 bg-card border border-border rounded-full p-1 w-fit">
        {PERIODS.map((p) => (
          <button key={p.k} data-testid={`period-${p.k}`} onClick={() => setPeriod(p.k)}
            className={`px-4 py-2 rounded-full text-xs font-semibold transition-all ${
              period === p.k
                ? "bg-foreground text-background shadow-sm"
                : "text-muted-foreground hover:text-foreground hover:bg-muted"
            }`}>
            {p.t}
          </button>
        ))}
      </div>

      <PerformanceCompactWidget period={period === "all" ? "month" : period} />
      <AlertsWidget />

      <SlaQueue items={slaQueue} />

      <PbxScoreCard pbx={pbx} />

      <PbxConsultantRanking
        ranking={pbxRanking}
        period={pbxRankingPeriod}
        onPeriodChange={setPbxRankingPeriod}
      />

      {/* 25/02/2026 — Painel DAF disponível para todos os utilizadores
          autenticados (era admin/gestor/direcao/dgo). Pedido do
          utilizador: dar visibilidade cruzada às cauções para todos
          conseguirem ver o estado antes de accionar clientes. */}
      <DepositSummaryPanel />

      {/* RTs pendentes — Consultor de Cuidados panel + Admin oversight */}
      {["consultor","gestor","admin","direcao","dgo"].includes(getUser()?.role) && <ConsultorRTsCard />}

      {/* Team volume — for coordinators / managers to balance the load */}
      {["admin","gestor","direcao","dgo"].includes(getUser()?.role) && <TeamVolumeWidget />}

      <QuickEstimateSimulator />

      {slaFirst && (
        <div className="bg-card border border-border rounded-2xl p-6 lg:p-8 mb-8" data-testid="sla-first-card">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center">
                <Zap className="w-4 h-4 text-foreground" />
              </div>
              <div>
                <h3 className="font-ui text-lg text-foreground">SLA de primeira resposta</h3>
                <div className="text-xs text-muted-foreground">Tempo entre criação do lead e a primeira ação registada</div>
              </div>
            </div>
            <Link to="/reports/daily" data-testid="link-daily-report" className="text-xs text-foreground hover:underline font-semibold">Relatório diário →</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
            <div className="bg-muted/40 border border-border rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1">% dentro dos 15 min</div>
              <div className={`font-ui text-3xl tracking-tight ${slaFirst.pct_within_15 >= 80 ? "text-[#059669]" : slaFirst.pct_within_15 >= 50 ? "text-[hsl(var(--warning))]" : "text-[hsl(var(--danger))]"}`}>{slaFirst.pct_within_15}%</div>
              <div className="text-xs text-muted-foreground mt-1">{slaFirst.total_leads} leads no período</div>
            </div>
            <div className="bg-muted/40 border border-border rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1">Tempo médio até 1ª ação</div>
              <div className="font-ui text-3xl tracking-tight text-foreground">{slaFirst.avg_minutes != null ? `${slaFirst.avg_minutes}m` : "—"}</div>
            </div>
            <div className="bg-muted/40 border border-border rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1">Top operador</div>
              <div className="font-ui text-lg tracking-tight text-foreground truncate">{slaFirst.ranking?.[0]?.name || "—"}</div>
              <div className="text-xs text-muted-foreground mt-1">{slaFirst.ranking?.[0]?.within_15 ?? 0} leads em ≤15m</div>
            </div>
          </div>
          {slaFirst.ranking?.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold border-b border-border">
                    <th className="text-left py-2 px-3">Operador</th>
                    <th className="text-right py-2 px-3">Leads</th>
                    <th className="text-right py-2 px-3">≤ 15 min</th>
                    <th className="text-right py-2 px-3">Média (min)</th>
                  </tr>
                </thead>
                <tbody>
                  {slaFirst.ranking.map(r => (
                    <tr key={r.id} className="border-b border-[#F1F5F9]" data-testid={`sla-row-${r.id}`}>
                      <td className="py-2 px-3">{r.name}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{r.leads}</td>
                      <td className="py-2 px-3 text-right tabular-nums font-semibold text-[#059669]">{r.within_15}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{r.avg_minutes != null ? r.avg_minutes : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-8">
        <Stat testid="kpi-leads" label="Leads" value={k.total_leads} icon={Users} index={0} />
        <Stat testid="kpi-estimates" label="Estimativas" value={k.estimates_sent} icon={FileCheck} index={1} />
        <Stat testid="kpi-scheduled" label="Reuniões agendadas" value={k.meetings_scheduled} icon={Calendar} index={2} />
        <Stat testid="kpi-conversion" label="Taxa de conversão" value={`${k.conversion_rate}%`} icon={TrendingUp} index={3} />
        <Stat testid="kpi-avg-value" label="Valor médio" value={`${k.avg_value.toLocaleString('pt-PT')} €`} icon={Euro} index={0} />
        <Stat testid="kpi-forecast" label="Receita prevista" value={`${k.revenue_forecast.toLocaleString('pt-PT')} €`} icon={TrendingUp} hint="Pipeline em curso" index={1} />
        <Stat testid="kpi-won" label="Receita adjudicada" value={`${k.revenue_won.toLocaleString('pt-PT')} €`} icon={Award} index={2} />
        <Stat testid="kpi-close-days" label="Dias até fecho" value={k.avg_close_days} icon={Clock} hint="Média" index={3} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 bg-card border border-border rounded-2xl p-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-ui text-lg text-foreground">Pipeline por estado</h3>
          </div>
          <div className="space-y-3">
            {Object.entries(STATUS_LABELS).map(([key, label]) => {
              const count = k.by_status[key] || 0;
              const pct = k.total_leads ? (count / k.total_leads) * 100 : 0;
              return (
                <div key={key} className="flex items-center gap-4">
                  <div className="w-40 text-sm text-muted-foreground">{label}</div>
                  <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-slate-900 to-slate-600" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="w-10 text-right text-sm text-muted-foreground tabular-nums">{count}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-card border border-border rounded-2xl p-8">
          <h3 className="font-ui text-lg text-foreground mb-6">Serviços mais procurados</h3>
          <div className="space-y-3">
            {Object.entries(k.services).sort((a,b)=>b[1]-a[1]).slice(0,7).map(([s, n]) => (
              <div key={s} className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{s}</span>
                <span className="text-muted-foreground tabular-nums">{n}</span>
              </div>
            ))}
            {Object.keys(k.services).length === 0 && <div className="text-sm text-muted-foreground/70">Sem dados ainda.</div>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-2xl p-8">
          <div className="flex items-center gap-2 mb-6"><MapPin className="w-4 h-4 text-foreground" /><h3 className="font-ui text-lg text-foreground">Distribuição por distrito</h3></div>
          <div className="space-y-3">
            {Object.entries(k.districts).sort((a,b)=>b[1]-a[1]).map(([d, n]) => (
              <div key={d} className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{d}</span>
                <span className="text-muted-foreground tabular-nums">{n}</span>
              </div>
            ))}
            {Object.keys(k.districts).length === 0 && <div className="text-sm text-muted-foreground/70">Sem dados ainda.</div>}
          </div>
        </div>
        <div className="bg-card border border-border rounded-2xl p-8">
          <h3 className="font-ui text-lg text-foreground mb-6">Motivos de perda</h3>
          <div className="space-y-3">
            {Object.entries(k.loss_reasons).sort((a,b)=>b[1]-a[1]).map(([r, n]) => (
              <div key={r} className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{r}</span>
                <span className="text-muted-foreground tabular-nums">{n}</span>
              </div>
            ))}
            {Object.keys(k.loss_reasons).length === 0 && <div className="text-sm text-muted-foreground/70">Sem perdas registadas.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
